import { useForm } from "@tanstack/react-form";
import { createFileRoute, useRouter, useSearch } from "@tanstack/react-router";
import { zodValidator } from "@tanstack/zod-adapter";
import { graphql, useMutation } from "react-relay";
import { toast } from "sonner";
import { z } from "zod";
import { Heading } from "@/components/heading";
import { Icon } from "@/components/icon";
import { Text, TextLink } from "@/components/text";
import { Button } from "@/components/ui/button";
import { Field, FieldError, FieldLabel } from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import { Spinner } from "@/components/ui/spinner";
import type { redefinirSenhaMutation } from "./__generated__/redefinirSenhaMutation.graphql";

export const Route = createFileRoute("/_auth/redefinir-senha")({
	component: RouteComponent,
	validateSearch: zodValidator(
		z.object({
			recovery_token: z.string(),
		}),
	),
});

function RouteComponent() {
	const { recovery_token: token } = useSearch({ from: Route.id });
	const router = useRouter();

	const [confirmPasswordReset] = useMutation<redefinirSenhaMutation>(graphql`
		mutation redefinirSenhaMutation($payload: ConfirmPasswordResetInput!) {
			confirmPasswordReset(payload: $payload)
		}
`);

	const form = useForm({
		defaultValues: {
			password: "",
			confirmPassword: "",
		},
		validators: {
			onChange: z
				.object({
					password: z
						.string()
						.min(6, "A senha deve ter pelo menos 6 caracteres"),
					confirmPassword: z.string().min(1, "Confirme sua senha"),
				})
				.refine((data) => data.password === data.confirmPassword, {
					message: "As senhas não coincidem",
					path: ["confirmPassword"],
				}),
		},
		onSubmit: async ({ value }) => {
			return confirmPasswordReset({
				variables: {
					payload: {
						code: token,
						password: value.password!,
					},
				},
				onCompleted: (response, errors) => {
					if (errors?.length) {
						toast.error(errors[0].message);
						return;
					}
					if (response.confirmPasswordReset) {
						toast.success("Senha redefinida com sucesso!");
						setTimeout(() => {
							router.navigate({
								to: "/login",
							});
						}, 2000);
					} else {
						toast.error("Não foi possível redefinir a senha.");
					}
				},
				onError: (err) => {
					if (
						err.message.includes("invalid") ||
						err.message.includes("expired")
					) {
						toast.error(
							"Token inválido ou expirado. Solicite uma nova recuperação de senha.",
						);
						setTimeout(() => {
							router.navigate({
								to: "/esqueci-a-senha",
							});
						}, 3000);
					} else {
						toast.error(
							"Não foi possível redefinir sua senha. Tente novamente.",
						);
					}
				},
			});
		},
	});

	return (
		<div className="flex justify-center w-full">
			<div className="flex w-full flex-col gap-8 justify-center p-6 max-w-[550px]">
				<Heading>Redefinir Senha</Heading>

				<Text className="text-left text-muted-foreground">
					Digite sua nova senha abaixo para redefinir o acesso à sua conta.
				</Text>

				<form
					className="flex flex-col gap-8"
					onSubmit={(e) => {
						e.preventDefault();
						e.stopPropagation();
						form.handleSubmit();
					}}
				>
					<form.Field name="password">
						{(field) => (
							<Field>
								<FieldLabel htmlFor={field.name}>Nova Senha</FieldLabel>
								<Input
									id={field.name}
									name={field.name}
									value={field.state.value}
									onBlur={field.handleBlur}
									onChange={(e) => field.handleChange(e.target.value)}
									type="password"
									placeholder="Digite sua nova senha"
									aria-invalid={
										!!(field.state.meta.isTouched && field.state.meta.errors[0])
									}
								/>

								{field.state.meta.isTouched && field.state.meta.errors[0] ? (
									<FieldError errors={field.state.meta.errors} />
								) : null}
							</Field>
						)}
					</form.Field>

					<form.Field name="confirmPassword">
						{(field) => (
							<Field>
								<FieldLabel htmlFor={field.name}>
									Confirmar Nova Senha
								</FieldLabel>
								<Input
									id={field.name}
									name={field.name}
									value={field.state.value}
									onBlur={field.handleBlur}
									onChange={(e) => field.handleChange(e.target.value)}
									type="password"
									placeholder="Confirme sua nova senha"
									aria-invalid={
										!!(field.state.meta.isTouched && field.state.meta.errors[0])
									}
								/>

								{field.state.meta.isTouched && field.state.meta.errors[0] ? (
									<FieldError errors={field.state.meta.errors} />
								) : null}
							</Field>
						)}
					</form.Field>

					<form.Subscribe
						selector={(state) => [state.isSubmitting, state.isSubmitSuccessful]}
					>
						{([isSubmitting, isSubmitSuccessful]) => (
							<div className="flex flex-col gap-6">
								<Button
									type="submit"
									color={isSubmitSuccessful ? "emerald" : "primary"}
									disabled={isSubmitting || isSubmitSuccessful}
									className="flex items-center justify-center gap-2"
								>
									{isSubmitSuccessful ? (
										<>
											Senha redefinida!{" "}
											<Icon name="CircleCheck" className="size-4" />
										</>
									) : (
										<>Redefinir senha {isSubmitting ? <Spinner /> : null}</>
									)}
								</Button>

								<Text className="font-medium">
									Lembrou sua senha?{" "}
									<TextLink to="/login" disabled={isSubmitting}>
										Voltar para login
									</TextLink>
								</Text>
							</div>
						)}
					</form.Subscribe>
				</form>
			</div>
		</div>
	);
}
